import type {
  StationOperationReason,
  StationOperationStatus,
  StationRegistryStatus,
  StationStatus,
  StationSyncStatus,
} from '@goelectric/shared-types';
import {
  STATION_OPERATION_META,
  STATION_REGISTRY_META,
  STATION_STATUS_META,
  STATION_SYNC_META,
} from '@goelectric/ui-components';
import { StatusBadgeShell, StatusIcon, useStatusColor } from '@/components/StatusBadgeShell';
import { OPERATION_REASON_LABELS } from '../utils/format';

/** Estado de gestión del cargador: color + icono + texto (WCAG, nunca solo color). */
export function StationStatusBadge({
  status,
  forceDark = false,
}: {
  status: StationStatus;
  forceDark?: boolean;
}) {
  const meta = STATION_STATUS_META[status];
  const color = useStatusColor(meta.color, meta.colorDark, forceDark);
  return (
    <StatusBadgeShell
      color={color}
      icon={<StatusIcon iconKey={meta.iconKey} />}
      label={meta.label}
      title={`${meta.label}: ${meta.description}`}
    />
  );
}

/** Estado de sincronización del cargador con CitrineOS (ADR 0024). */
export function StationSyncBadge({
  status,
  forceDark = false,
}: {
  status: StationSyncStatus;
  forceDark?: boolean;
}) {
  const meta = STATION_SYNC_META[status];
  const color = useStatusColor(meta.color, meta.colorDark, forceDark);
  return (
    <StatusBadgeShell
      color={color}
      icon={<StatusIcon iconKey={meta.iconKey} />}
      label={meta.label}
      title={`${meta.label}: ${meta.description}`}
    />
  );
}

/** Estado del registro (derivado): pre-registrado, sincronizado, error o inhabilitado. */
export function StationRegistryBadge({
  status,
  forceDark = false,
}: {
  status: StationRegistryStatus;
  forceDark?: boolean;
}) {
  // Defensa en el borde: un backend viejo (sin el campo derivado) no debe romper la vista.
  const meta = STATION_REGISTRY_META[status];
  if (!meta) {
    return <span aria-label="Estado de registro desconocido">—</span>;
  }
  const color = useStatusColor(meta.color, meta.colorDark, forceDark);
  return (
    <StatusBadgeShell
      color={color}
      icon={<StatusIcon iconKey={meta.iconKey} />}
      label={meta.label}
      title={`${meta.label}: ${meta.description}`}
    />
  );
}

/**
 * Estado operativo vivo. `—` cuando el registro no está `SYNCHRONIZED` (no aplica).
 * El título incluye el motivo cuando está `No disponible`.
 */
export function StationOperationBadge({
  status,
  reason = null,
  forceDark = false,
}: {
  status: StationOperationStatus | null;
  reason?: StationOperationReason | null;
  forceDark?: boolean;
}) {
  if (!status) {
    return <span aria-label="Sin estado operativo">—</span>;
  }
  const meta = STATION_OPERATION_META[status];
  if (!meta) {
    return <span aria-label="Estado operativo desconocido">—</span>;
  }
  const color = useStatusColor(meta.color, meta.colorDark, forceDark);
  const detail = reason ? OPERATION_REASON_LABELS[reason] : meta.description;
  return (
    <StatusBadgeShell
      color={color}
      icon={<StatusIcon iconKey={meta.iconKey} />}
      label={meta.label}
      title={`${meta.label}: ${detail}`}
    />
  );
}
